import { useAuth } from "@features/auth/contexts/AuthContext";
import { BankAccountUpdate } from "@features/banking/components/BankingForm";
import { useTranslation } from "react-i18next";
import HeadLayout from "@shared/layouts/HeadLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { CreditCard } from "lucide-react";

const SetupAccount = () => {
  const auth = useAuth();
  const { t } = useTranslation();

  // Check if user has banking info and if it's verified
  const hasBankingInfo =
    auth.user?.bankAccount?.bankName && auth.user?.bankAccount?.bankNumber;
  const isBankingVerified =
    auth.user?.bankAccount?.verified || auth.user?.bankingStatus === "approved";
  const isBankingPending = auth.user?.bankingStatus === "pending";
  const isBankingRejected = auth.user?.bankingStatus === "rejected";

  return (
    <>
      <HeadLayout title={t("setupAccount.setupBankAccount")} />
      <div className="container mx-auto p-6">
        {hasBankingInfo && isBankingVerified ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4 text-green-600 dark:text-green-300">
              {t("setupAccount.bankAccountVerified")}
            </h1>
            <p className="text-muted-foreground mb-6">
              {t("setupAccount.bankAccountComplete")}
            </p>

            {/* Allow editing even when verified */}
            <div className="max-w-md mx-auto">
              <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-300">
                        {t("banking.updateBankingInfo")}
                      </h3>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t("banking.updateBankingDescription")}
                    </p>
                    <div className="max-w-2xl mx-auto">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            {t("banking.editBankingInformation")}
                          </CardTitle>
                          <CardDescription>
                            {t("banking.editBankingDescription")}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <BankAccountUpdate initialData={auth.user} />
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : hasBankingInfo && isBankingPending ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-yellow-600 dark:text-yellow-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4 text-yellow-600 dark:text-yellow-300">
              {t("setupAccount.bankAccountPending")}
            </h1>
            <p className="text-muted-foreground">
              {t("setupAccount.bankAccountPendingDescription")}
            </p>
          </div>
        ) : hasBankingInfo && isBankingRejected ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-300">
              {t("setupAccount.bankAccountRejected")}
            </h1>
            <p className="text-muted-foreground mb-4">
              {t("setupAccount.bankAccountRejectedDescription")}
            </p>
            {auth.user?.bankingRejectionReason && (
              <div className="mb-6 p-4 border border-red-200 bg-red-50 rounded-lg dark:border-red-800 dark:bg-red-950/20">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>{t("banking.reason")}:</strong>{" "}
                  {auth.user.bankingRejectionReason}
                </p>
              </div>
            )}
            <div className="max-w-2xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t("setupAccount.resubmitBankingInformation")}
                  </CardTitle>
                  <CardDescription>
                    {t("setupAccount.resubmitBankingDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BankAccountUpdate initialData={auth.user} />
                </CardContent>
              </Card>
            </div>
          </div>
        ) : hasBankingInfo && !isBankingVerified ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-orange-600 dark:text-orange-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4 text-orange-600 dark:text-orange-300">
              {t("setupAccount.bankAccountUnverified")}
            </h1>
            <p className="text-muted-foreground">
              {t("setupAccount.bankAccountUnverifiedDescription")}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">
                {t("setupAccount.setupBankAccount")}
              </h1>
              <p className="text-muted-foreground">
                {t("setupAccount.setupBankAccountDescription")}
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t("setupAccount.bankingInformation")}
                  </CardTitle>
                  <CardDescription>
                    {t("setupAccount.bankingDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BankAccountUpdate initialData={auth.user} />
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SetupAccount;
